import { useMutation, useQueryClient } from '@tanstack/react-query';
import { accessGrantsApi } from '../api';
import type {
  AccessGrantsQuery,
  AccessGrantsResponse,
  CreateAccessGrantRequest,
  UpdateAccessGrantRequest,
} from '../api/types';
import { useCustomQuery } from './useCustomQuery';

/**
 * Query key factory for access grants. Lists are scoped by the target object
 * so invalidation across objects doesn't cause unnecessary refetches.
 */
export const accessGrantQueryKeys = {
  all: ['access-grants'] as const,
  list: (query: AccessGrantsQuery) =>
    [
      ...accessGrantQueryKeys.all,
      'list',
      {
        entityScopeId: query.entityScopeId !== undefined ? String(query.entityScopeId) : null,
        objectType: query.objectType ?? null,
        objectId: query.objectId !== undefined ? String(query.objectId) : null,
        includeRevoked: !!query.includeRevoked,
        search: query.search ?? '',
      },
    ] as const,
  detail: (id: number) => [...accessGrantQueryKeys.all, 'detail', id] as const,
  forObject: (objectType: string, objectId: string | number) =>
    [...accessGrantQueryKeys.all, 'object', objectType, String(objectId)] as const,
};

/**
 * Fetch access grants. Pass either `entityScopeId` (for all grants belonging
 * to an entity and its initiatives) or `objectType` + `objectId`.
 */
export function useAccessGrants(query: AccessGrantsQuery) {
  const enabled =
    query.entityScopeId !== undefined ||
    (!!query.objectType && query.objectId !== undefined && query.objectId !== '');
  return useCustomQuery({
    queryKey: accessGrantQueryKeys.list(query),
    queryFn: () => accessGrantsApi.getGrants(query),
    enabled,
    staleTime: 1000 * 30,
  });
}

/**
 * Create a new access grant. The raw token is in the response exactly once;
 * surface it to the user immediately and discard the value.
 */
export function useCreateAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAccessGrantRequest) => accessGrantsApi.createGrant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGrantQueryKeys.all });
    },
  });
}

export function useUpdateAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAccessGrantRequest }) =>
      accessGrantsApi.updateGrant(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(accessGrantQueryKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: accessGrantQueryKeys.all });
    },
  });
}

/**
 * Revoke (soft delete) a grant. Optimistically marks the row revoked in any
 * cached lists so the UI updates instantly.
 */
export function useRevokeAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accessGrantsApi.revokeGrant(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: accessGrantQueryKeys.all });
      const snapshots = queryClient.getQueriesData<AccessGrantsResponse>({
        queryKey: accessGrantQueryKeys.all,
      });
      const revokedAt = new Date().toISOString();
      queryClient.setQueriesData<AccessGrantsResponse>(
        { queryKey: accessGrantQueryKeys.all },
        (old) => {
          if (!old?.grants) return old;
          return {
            ...old,
            grants: old.grants.map((g) => (g.id === id ? { ...g, revokedAt } : g)),
          };
        }
      );
      return { snapshots };
    },
    onError: (_err, _id, context) => {
      context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accessGrantQueryKeys.all });
    },
  });
}
